import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, RefreshCw, AlertTriangle, Download, Flame, Beef, Wheat, Droplet,
  CalendarDays, FileText, PartyPopper,
} from 'lucide-react';
import ProgramBuildingLoader from '../calculator/ProgramBuildingLoader';
import PaidContent from '../calculator/PaidContent';
import { printPlan } from '../calculator/printPlan';
import { MealPlanCalendar } from './MealPlanCalendar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_ATTEMPTS = 90; // ~7.5 min de polling, aligné sur le timeout backend

/**
 * Consultation 100% in-app d'un plan alimentaire payant — remplace l'ancien renvoi vers
 * la page publique du calculateur (qui « sortait » le client de l'app sans retour
 * possible). Réutilise les mêmes composants premium (PaidContent, MealPlanCalendar,
 * ProgramBuildingLoader) que la page marketing, mais dans le contexte authentifié de
 * /mon-espace, avec une révélation "wahou" une fois le plan prêt.
 */
export const MealPlanResultView = ({ calculationId, packageType = 'meal_plan', en, onBack }) => {
  const language = en ? 'en' : 'fr';
  const L = (fr, eng) => (en ? eng : fr);
  const [status, setStatus] = useState('loading'); // loading | generating | ready | error
  const [genStep, setGenStep] = useState('plan');
  const [data, setData] = useState(null);
  const [reveal, setReveal] = useState(false);
  const [view, setView] = useState('calendar');
  const [retryKey, setRetryKey] = useState(0);
  const timerRef = useRef(null);
  const attemptsRef = useRef(0);
  const wasGeneratingRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    attemptsRef.current = 0;
    wasGeneratingRef.current = false;
    setStatus('loading');
    setData(null);

    const fetchOnce = async () => {
      const res = await fetch(`${BACKEND_URL}/api/calorie-calculator/results/${calculationId}?package=${packageType}&origin=${encodeURIComponent(window.location.origin)}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    };

    const markReady = (d) => {
      setGenStep('done');
      setData(d);
      setStatus('ready');
      // La révélation "wahou" ne joue qu'à la toute fin d'une génération réelle observée
      // dans cette session — pas chaque fois qu'on rouvre un plan déjà prêt.
      if (wasGeneratingRef.current) {
        setReveal(true);
        setTimeout(() => { if (!cancelled) setReveal(false); }, 2800);
      }
    };

    const tick = async () => {
      try {
        const d = await fetchOnce();
        if (cancelled) return;
        if (d.status === 'error') { setStatus('error'); return; }
        if (d.progress_step) setGenStep(d.progress_step);
        if (d.type === 'free' || d.status === 'ready' || d.meal_plan) {
          markReady(d);
          return;
        }
        setStatus('generating');
        wasGeneratingRef.current = true;
        attemptsRef.current += 1;
        if (attemptsRef.current > MAX_ATTEMPTS) { setStatus('error'); return; }
        timerRef.current = setTimeout(tick, 5000);
      } catch {
        attemptsRef.current += 1;
        if (cancelled) return;
        if (attemptsRef.current > MAX_ATTEMPTS) setStatus('error');
        else timerRef.current = setTimeout(tick, 5000);
      }
    };
    tick();
    return () => { cancelled = true; if (timerRef.current) clearTimeout(timerRef.current); };
  }, [calculationId, packageType, retryKey]);

  const results = data?.results;
  const macros = results?.macros || {};
  const structured = data?.meal_plan_structured;
  const hasCalendar = !!(structured?.days?.length);
  const download = (title, content, isMeal) => printPlan(title, content, isMeal);

  return (
    <div data-testid="meal-plan-result-view">
      <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white mb-5 transition-colors" data-testid="meal-plan-result-back-btn">
        <ArrowLeft className="w-4 h-4" /> {L('Retour', 'Back')}
      </button>

      {(status === 'loading' || status === 'generating') && (
        <div className="rounded-3xl border border-white/10 bg-zinc-900/60" data-testid="meal-plan-result-generating">
          <ProgramBuildingLoader language={language} progressStep={genStep} />
        </div>
      )}

      {status === 'error' && (
        <div className="rounded-3xl border border-red-500/30 bg-red-500/5 p-8 text-center" data-testid="meal-plan-result-error">
          <AlertTriangle className="w-10 h-10 text-red-400 mx-auto mb-3" />
          <h3 className="text-white font-bold text-lg mb-1">{L('La génération a échoué', 'Generation failed')}</h3>
          <p className="text-zinc-400 text-sm mb-5">{L('Cela peut arriver ponctuellement — vous pouvez relancer la création sans perdre vos crédits.', 'This can happen occasionally — you can retry without losing your credits.')}</p>
          <button onClick={() => setRetryKey((k) => k + 1)} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-white bg-gradient-to-r from-primary to-pink-500" data-testid="meal-plan-result-retry-btn">
            <RefreshCw className="w-4 h-4" /> {L('Réessayer', 'Retry')}
          </button>
        </div>
      )}

      <AnimatePresence>
        {reveal && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[300] flex items-center justify-center bg-black/85 backdrop-blur-sm"
            data-testid="meal-plan-result-reveal"
          >
            {[...Array(14)].map((_, i) => (
              <motion.span
                key={i}
                className="absolute w-2 h-2 rounded-full"
                style={{ background: i % 2 ? '#FBBF24' : '#E91E63', left: '50%', top: '50%' }}
                initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
                animate={{
                  x: Math.cos((i / 14) * Math.PI * 2) * 220,
                  y: Math.sin((i / 14) * Math.PI * 2) * 220,
                  opacity: 0, scale: 0.4,
                }}
                transition={{ duration: 1.4, ease: 'easeOut', delay: 0.15 }}
              />
            ))}
            <motion.div
              initial={{ scale: 0.4, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 200, damping: 14 }}
              className="text-center px-6"
            >
              <motion.div
                animate={{ rotate: [0, -8, 8, 0] }} transition={{ duration: 0.6, repeat: 2 }}
                className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-yellow-400 to-primary flex items-center justify-center shadow-[0_0_50px_rgba(233,30,99,0.6)]"
              >
                <PartyPopper className="w-10 h-10 text-white" />
              </motion.div>
              <h2 className="calc-display text-2xl sm:text-3xl font-black text-white">
                {L('Votre plan est prêt !', 'Your plan is ready!')}
              </h2>
              <p className="text-zinc-400 text-sm mt-1">{L('Découvrez vos menus sur-mesure ci-dessous', 'Discover your custom menus below')}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {status === 'ready' && results && (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} data-testid="meal-plan-result-ready">
          {/* Stat summary */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <div className="rounded-2xl border border-white/10 bg-zinc-900/60 p-3.5 text-center" data-testid="meal-plan-stat-calories">
              <Flame className="w-4 h-4 text-primary mx-auto mb-1" />
              <p className="text-lg font-black text-white">{Math.round(results.target_calories)}</p>
              <p className="text-[11px] text-zinc-500">kcal</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-zinc-900/60 p-3.5 text-center" data-testid="meal-plan-stat-protein">
              <Beef className="w-4 h-4 text-pink-400 mx-auto mb-1" />
              <p className="text-lg font-black text-white">{Math.round(macros.protein_g || 0)}g</p>
              <p className="text-[11px] text-zinc-500">{L('protéines', 'protein')}</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-zinc-900/60 p-3.5 text-center" data-testid="meal-plan-stat-carbs">
              <Wheat className="w-4 h-4 text-yellow-400 mx-auto mb-1" />
              <p className="text-lg font-black text-white">{Math.round(macros.carbs_g || 0)}g</p>
              <p className="text-[11px] text-zinc-500">{L('glucides', 'carbs')}</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-zinc-900/60 p-3.5 text-center" data-testid="meal-plan-stat-fat">
              <Droplet className="w-4 h-4 text-emerald-400 mx-auto mb-1" />
              <p className="text-lg font-black text-white">{Math.round(macros.fat_g || 0)}g</p>
              <p className="text-[11px] text-zinc-500">{L('lipides', 'fat')}</p>
            </div>
          </div>

          {/* Pro analysis (if unlocked as part of the meal plan package) */}
          {data.pro_analysis && (
            <div className="mb-10">
              <div className="flex items-center justify-end mb-4">
                <button onClick={() => download(L('Analyse Pro', 'Pro Analysis'), data.pro_analysis, false)} className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary to-pink-500 text-white rounded-xl text-xs font-semibold hover:scale-105 transition-transform" data-testid="meal-plan-download-pro-btn">
                  <Download className="w-3.5 h-3.5" /> PDF
                </button>
              </div>
              <PaidContent content={data.pro_analysis} type="pro" language={language} />
            </div>
          )}

          {/* Meal plan */}
          {data.meal_plan && (
            <div>
              <div className="flex items-center justify-end gap-2 mb-4 flex-wrap">
                {hasCalendar && (
                  <div className="flex items-center gap-1 p-1 rounded-xl bg-zinc-900 border border-white/10 mr-auto" data-testid="meal-plan-view-toggle">
                    <button onClick={() => setView('calendar')} className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-colors ${view === 'calendar' ? 'bg-primary text-white' : 'text-zinc-400'}`} data-testid="meal-plan-view-calendar-btn">
                      <CalendarDays className="w-3.5 h-3.5" /> {L('Calendrier', 'Calendar')}
                    </button>
                    <button onClick={() => setView('text')} className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-colors ${view === 'text' ? 'bg-primary text-white' : 'text-zinc-400'}`} data-testid="meal-plan-view-text-btn">
                      <FileText className="w-3.5 h-3.5" /> {L('Texte', 'Text')}
                    </button>
                  </div>
                )}
                <button onClick={() => download(L('Plan Alimentaire', 'Meal Plan'), data.meal_plan, true)} className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-yellow-400 to-primary text-black rounded-xl text-xs font-semibold hover:scale-105 transition-transform" data-testid="meal-plan-download-btn">
                  <Download className="w-3.5 h-3.5" /> PDF
                </button>
              </div>
              {hasCalendar && view === 'calendar' ? (
                <MealPlanCalendar calculationId={calculationId} initialDays={structured.days} initialShoppingList={structured.shopping_list} en={en} />
              ) : (
                <PaidContent content={data.meal_plan} type="meal_plan" language={language} />
              )}
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
};

export default MealPlanResultView;
